import React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import {
  FONT_SIZES,
  FONT_WEIGHTS,
} from '../../../Theme';

export const MARKETING_MAX_WIDTH = 1280;
/** Signed-in home column. Slightly wider than the 1480px header card. */
export const AUTHENTICATED_LANDING_MAX_WIDTH = 1520;
/** Align with CategoryShowcase CategoryWrapper (max-width + horizontal padding). */
export const MARKETING_CATEGORY_ALIGNED_MAX_WIDTH = MARKETING_MAX_WIDTH;
export const MARKETING_CATEGORY_ALIGNED_PADDING_X = 16;
/** Minimum space between the framed column and the viewport from the sm breakpoint up. */
export const MARKETING_PAGE_GUTTER_PX = 48;
/** Minimum space between the signed-in frame and the viewport, same idea as the guest gutter. */
const AUTHENTICATED_LANDING_INSET_PX = MARKETING_PAGE_GUTTER_PX;
/** Phone inset. Wider than the old 16px so the guest column stays off the screen edges. */
export const MARKETING_PAGE_GUTTER_MOBILE_PX = 32;
/** Desktop column: capped at the frame, and never closer than the gutter. */
export const MARKETING_COLUMN_WIDTH = `min(${MARKETING_MAX_WIDTH}px, calc(100% - ${MARKETING_PAGE_GUTTER_PX * 2}px))`;
/** Signed-in desktop column: capped at the signed-in frame. */
export const AUTHENTICATED_LANDING_COLUMN_WIDTH = `min(${AUTHENTICATED_LANDING_MAX_WIDTH}px, calc(100% - ${AUTHENTICATED_LANDING_INSET_PX * 2}px))`;
/** Phone column: the same idea, with the smaller inset. */
export const MARKETING_COLUMN_WIDTH_MOBILE = `calc(100% - ${MARKETING_PAGE_GUTTER_MOBILE_PX * 2}px)`;
/**
 * Signed-in phone column. Full width, so the section padding is the only inset
 * and lines up with the mobile brand bar instead of the guest frame.
 */
export const AUTHENTICATED_LANDING_COLUMN_WIDTH_MOBILE = '100%';
export const BRAND_LANDING_SECTION_GAP = 72;
/** Space between signed-in home rows (Recently Viewed, Favorited, and the rows below). */
export const AUTHENTICATED_LANDING_SECTION_GAP = 28;
/** Space between campaign, brand, and influencer card rows only. */
export const AUTHENTICATED_LANDING_CARD_SECTION_GAP = 16;
/** Extra air between the signed-in category row, banner, and questions. */
export const AUTHENTICATED_LANDING_BOTTOM_GAP = 40;
/** Vertical padding inside each logged-out band. The hairline replaces the old section gap. */
export const GUEST_LANDING_BAND_PADDING_Y_MOBILE = 72;
export const GUEST_LANDING_BAND_PADDING_Y = 96;
/** Compact vertical padding inside the final CTA box. */
export const FINAL_CTA_BAR_PADDING_Y = 32;
/** Horizontal inset so FAQ and legal CTA copy sits clear of the frame. */
export const FINAL_CTA_FRAME_PADDING_X = 40;
export const FINAL_CTA_FRAME_PADDING_X_WIDE = 56;

export const signedInHeroSectionLabelSx = {
  m: 0,
  mb: 1.5,
  fontSize: FONT_SIZES.title,
  fontWeight: FONT_WEIGHTS.medium,
  color: 'text.primary',
  letterSpacing: '-0.02em',
  lineHeight: 1.3,
  textAlign: 'left',
};

export const marketingCategoryAlignedContainerSx = {
  maxWidth: MARKETING_CATEGORY_ALIGNED_MAX_WIDTH,
  mx: 'auto',
  px: `${MARKETING_CATEGORY_ALIGNED_PADDING_X}px`,
  width: { xs: MARKETING_COLUMN_WIDTH_MOBILE, sm: MARKETING_COLUMN_WIDTH },
  boxSizing: 'border-box',
};

export const authenticatedCategoryAlignedContainerSx = {
  ...marketingCategoryAlignedContainerSx,
  maxWidth: AUTHENTICATED_LANDING_MAX_WIDTH,
  width: { xs: AUTHENTICATED_LANDING_COLUMN_WIDTH_MOBILE, sm: AUTHENTICATED_LANDING_COLUMN_WIDTH },
};

export const authenticatedLandingColumnSx = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  gap: `${AUTHENTICATED_LANDING_SECTION_GAP}px`,
};

/** Signed-in home fills the space above the footer so the CTA rules meet it. */
export const signedInLandingFrameSx = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
};

export const authenticatedLandingCardRowsSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${AUTHENTICATED_LANDING_CARD_SECTION_GAP}px`,
};

export const authenticatedLandingBottomSectionSx = {
  marginTop: `${AUTHENTICATED_LANDING_BOTTOM_GAP}px`,
};

export const guestLandingColumnSx = {
  display: 'flex',
  flexDirection: 'column',
};

const guestLandingRuleInset = `max(${MARKETING_PAGE_GUTTER_PX}px, calc((100% - ${MARKETING_MAX_WIDTH}px) / 2))`;
const authenticatedLandingRuleInset = `max(${AUTHENTICATED_LANDING_INSET_PX}px, calc((100% - ${AUTHENTICATED_LANDING_MAX_WIDTH}px) / 2))`;
/** Keep the phone frame off the screen edges. */
const GUEST_LANDING_MOBILE_INSET = MARKETING_PAGE_GUTTER_MOBILE_PX;
const AUTHENTICATED_LANDING_MOBILE_INSET = MARKETING_CATEGORY_ALIGNED_PADDING_X;
const GUEST_LANDING_INTERSECTION_DOT = 3;
/** How far a hairline dissolves before it meets a dot. */
const GUEST_LANDING_DOT_FADE = 18;

const GUEST_LANDING_RULE_CONFIG = {
  ruleInset: guestLandingRuleInset,
  mobileInset: GUEST_LANDING_MOBILE_INSET,
  intersectionTestId: 'guest-landing-intersection',
};

const AUTHENTICATED_LANDING_RULE_CONFIG = {
  ruleInset: authenticatedLandingRuleInset,
  mobileInset: AUTHENTICATED_LANDING_MOBILE_INSET,
  intersectionTestId: 'authenticated-landing-intersection',
};

function intersectionDotSx(side, edge, { ruleInset, mobileInset }) {
  const shiftX = side === 'right' ? '50%' : '-50%';
  const shiftY = '-50%';
  const sx = {
    position: 'absolute',
    width: GUEST_LANDING_INTERSECTION_DOT,
    height: GUEST_LANDING_INTERSECTION_DOT,
    borderRadius: '50%',
    bgcolor: 'text.secondary',
    pointerEvents: 'none',
    zIndex: 4,
    transform: `translate(${shiftX}, ${shiftY})`,
  };

  if (edge === 'footer') {
    sx.top = '-0.5px';
  } else {
    sx.top = '0.5px';
  }

  const mobileEdge = `calc(${mobileInset}px + 0.5px)`;
  const columnEdge = `calc(${ruleInset} + 0.5px)`;
  if (side === 'right') {
    sx.right = { xs: mobileEdge, sm: columnEdge };
  } else {
    sx.left = { xs: mobileEdge, sm: columnEdge };
  }

  return sx;
}

function LandingColumnIntersectionDot({ side, edge = 'top', ruleConfig }) {
  return (
    <Box
      aria-hidden
      data-testid={ruleConfig.intersectionTestId}
      sx={intersectionDotSx(side, edge, ruleConfig)}
    />
  );
}

function horizontalRuleBackground(theme, ruleInset = guestLandingRuleInset) {
  const line = theme.palette.divider;
  const clear = alpha(line, 0);
  const fade = `${GUEST_LANDING_DOT_FADE}px`;
  const leftDot = ruleInset;
  const rightDot = `calc(100% - ${ruleInset})`;
  return [
    `linear-gradient(to right, ${line} 0, ${line} calc(${leftDot} - ${fade})`,
    `${clear} ${leftDot}`,
    `${line} calc(${leftDot} + ${fade})`,
    `${line} calc(${rightDot} - ${fade})`,
    `${clear} ${rightDot}`,
    `${line} calc(${rightDot} + ${fade}), ${line} 100%)`,
  ].join(', ');
}

/** Fade into the top dots, then stay solid so the sides meet the footer line. */
function verticalRuleBackground(theme) {
  const line = theme.palette.divider;
  const clear = alpha(line, 0);
  const fade = `${GUEST_LANDING_DOT_FADE}px`;
  return `linear-gradient(to bottom, ${clear}, ${line} ${fade}, ${line} 100%)`;
}

function landingColumnHairlineSx(theme, top, ruleConfig) {
  const { ruleInset, mobileInset } = ruleConfig;
  return {
    position: 'absolute',
    top,
    left: 0,
    right: 0,
    height: '1px',
    pointerEvents: 'none',
    zIndex: 2,
    backgroundImage: horizontalRuleBackground(theme, `${mobileInset}px`),
    [theme.breakpoints.up('sm')]: {
      backgroundImage: horizontalRuleBackground(theme, ruleInset),
    },
  };
}

function LandingColumnTopRule({ ruleConfig }) {
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        ...landingColumnHairlineSx(theme, 0, ruleConfig),
        backgroundColor: alpha(theme.palette.divider, 0),
      })}
    />
  );
}

function landingColumnFooterHairlineSx(theme, ruleConfig) {
  return landingColumnHairlineSx(theme, '-1px', ruleConfig);
}

function landingColumnFooterHairline(ruleConfig) {
  return (
    <>
      <Box
        aria-hidden
        sx={(theme) => landingColumnFooterHairlineSx(theme, ruleConfig)}
      />
      <LandingColumnIntersectionDot side="left" edge="footer" ruleConfig={ruleConfig} />
      <LandingColumnIntersectionDot side="right" edge="footer" ruleConfig={ruleConfig} />
    </>
  );
}

/** Dots on the footer hairline, aligned to the same column as the page rules. */
export function GuestLandingFooterDots() {
  return landingColumnFooterHairline(GUEST_LANDING_RULE_CONFIG);
}

/** Closing hairline above the footer on the signed-in home. */
export function AuthenticatedLandingFooterHairline() {
  return landingColumnFooterHairline(AUTHENTICATED_LANDING_RULE_CONFIG);
}

function LandingColumnVerticalRule({ side, ruleConfig }) {
  const mobileEdge = `${ruleConfig.mobileInset}px`;
  const sideSx = side === 'right'
    ? { right: { xs: mobileEdge, sm: ruleConfig.ruleInset } }
    : { left: { xs: mobileEdge, sm: ruleConfig.ruleInset } };

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: 0,
        bottom: '-1px',
        width: '1px',
        pointerEvents: 'none',
        zIndex: 2,
        backgroundImage: (theme) => verticalRuleBackground(theme),
        ...sideSx,
      }}
    />
  );
}

function LandingColumnBandRules({ ruleConfig }) {
  return (
    <>
      <LandingColumnTopRule ruleConfig={ruleConfig} />
      <LandingColumnVerticalRule side="left" ruleConfig={ruleConfig} />
      <LandingColumnVerticalRule side="right" ruleConfig={ruleConfig} />
      <LandingColumnIntersectionDot side="left" ruleConfig={ruleConfig} />
      <LandingColumnIntersectionDot side="right" ruleConfig={ruleConfig} />
    </>
  );
}

/** Column frame for the logged-out home. The footer border is the closing rule. */
export const guestLandingFrameSx = {
  position: 'relative',
};

/** FAQ, privacy, and terms: sticky legal shell plus closing CTA band. */
const GUEST_DOCUMENT_PATHS = new Set([
  '/faq',
  '/privacy-policy',
  '/legal/privacy',
  '/terms-and-conditions',
  '/legal/terms',
]);

export function isGuestDocumentFramePath(pathname) {
  return GUEST_DOCUMENT_PATHS.has(pathname);
}

function GuestLandingRules() {
  return <LandingColumnBandRules ruleConfig={GUEST_LANDING_RULE_CONFIG} />;
}

/** Signed-in final CTA frame. The footer hairline is the closing rule. */
export function AuthenticatedLandingBandRules() {
  return <LandingColumnBandRules ruleConfig={AUTHENTICATED_LANDING_RULE_CONFIG} />;
}

/** FAQ / legal document column; hairlines sit on the closing CTA band and footer. */
export function GuestLandingDocumentFrame({ children }) {
  return (
    <Box
      data-testid="guest-landing-frame"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        width: '100%',
        mx: 'auto',
        '@media (min-width: 1025px)': {
          width: MARKETING_COLUMN_WIDTH,
        },
      }}
    >
      {children}
    </Box>
  );
}

export const guestLandingBandSx = {
  position: 'relative',
  paddingTop: {
    xs: `${GUEST_LANDING_BAND_PADDING_Y_MOBILE}px`,
    md: `${GUEST_LANDING_BAND_PADDING_Y}px`,
  },
  paddingBottom: {
    xs: `${GUEST_LANDING_BAND_PADDING_Y_MOBILE}px`,
    md: `${GUEST_LANDING_BAND_PADDING_Y}px`,
  },
};

export function GuestLandingBand({
  children,
  component = 'div',
  ariaLabelledBy,
  showTopRule = true,
  paddingY,
  sx,
}) {
  let bandSx = guestLandingBandSx;
  if (paddingY != null) {
    bandSx = {
      ...guestLandingBandSx,
      paddingTop: paddingY,
      paddingBottom: paddingY,
    };
  }

  return (
    <Box
      component={component}
      aria-labelledby={ariaLabelledBy}
      data-testid="guest-landing-band"
      sx={[bandSx, sx]}
    >
      {showTopRule ? <GuestLandingRules /> : null}
      {children}
    </Box>
  );
}

export const MarketingPageRoot = styled(Box)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
}));

export const MarketingContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$signedIn',
})(({ theme, $signedIn }) => ({
  maxWidth: $signedIn ? AUTHENTICATED_LANDING_MAX_WIDTH : MARKETING_MAX_WIDTH,
  margin: '0 auto',
  padding: '0 28px',
  width: $signedIn ? AUTHENTICATED_LANDING_COLUMN_WIDTH_MOBILE : MARKETING_COLUMN_WIDTH_MOBILE,
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    width: $signedIn ? AUTHENTICATED_LANDING_COLUMN_WIDTH : MARKETING_COLUMN_WIDTH,
  },
  [theme.breakpoints.down('sm')]: {
    padding: $signedIn ? `0 ${MARKETING_CATEGORY_ALIGNED_PADDING_X}px` : '0 20px',
  },
}));

export const MarketingSection = styled(Box)({});

export const MarketingHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$large',
})(({ theme, $large }) => ({
  margin: 0,
  fontFamily: theme.typography.h2.fontFamily,
  fontWeight: FONT_WEIGHTS.medium,
  letterSpacing: '-0.02em',
  lineHeight: $large ? 1.04 : 1.1,
  fontSize: FONT_SIZES.heading,
}));

export const MarketingSubheading = styled(Typography)(({ theme }) => ({
  margin: '10px 0 0',
  fontSize: FONT_SIZES.body,
  fontWeight: FONT_WEIGHTS.regular,
  lineHeight: 1.5,
  color: theme.palette.text.secondary,
}));
